// src/services/quiz/answerStateManager.ts
import { ModuleQuestion, QuestionType } from "../../types/ProgramModel";

/**
 * Answer types for each question type
 */
export interface AnswerFormats {
  "1": number; // Multiple Choice - single selection index
  "2": string; // Reading Comprehension - selected answer text
  "3": number[]; // Audio Listening - multiple selection indices
  "4": string[]; // Sentence Building - ordered array of segments
  "6": Record<string, string | number>; // Reference Reading - multi-part answers
  "8": string[]; // Switch Card - matched pairs
  "9": Record<string, string>; // Parts of Speech - word to part mapping
  "10": Record<string, string>; // Fill-in-blank MC - blank id to answer
  "11": Blob | string | null; // Speaking Practice - audio recording or URL
  "12": null; // Instructional Content - no answer needed
  "13": number[]; // True/False Selection - indices of true statements
}

export type QuestionAnswer = AnswerFormats[keyof AnswerFormats];

/**
 * Validation result for answer checking
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  suggestions?: string[];
  formattedAnswer?: any;
}

/**
 * Answer state manager for quiz questions
 */
export class AnswerStateManager {
  private userAnswers: Map<string, any>;
  private validationCache: Map<string, ValidationResult>;

  constructor(initialAnswers?: Map<string, any>) {
    this.userAnswers = initialAnswers || new Map();
    this.validationCache = new Map();
  }

  /**
   * Validate answer format for specific question type
   */
  public validateAnswer(
    questionType: string,
    answer: any,
    question?: any
  ): ValidationResult {
    // Handle skipped answers first
    if (answer === "SKIPPED") {
      return {
        isValid: true,
        errors: [],
        formattedAnswer: "SKIPPED",
      };
    }

    // Special case for Type 12 (Instructional) - always valid
    if (questionType === "12") {
      return this.validateInstructionalContent(answer, question);
    }

    // Special case for Type 11 (Speaking) - handle null specially
    if (questionType === "11") {
      return this.validateSpeakingPractice(answer, question);
    }

    // Handle edge cases for other types
    if (answer === undefined || answer === null) {
      return {
        isValid: false,
        errors: ["Answer is required"],
        suggestions: ["Please provide an answer before continuing"],
      };
    }

    switch (questionType) {
      case "1": // Multiple Choice
        return this.validateMultipleChoice(answer, question);

      case "2": // Reading Comprehension
        return this.validateReadingComprehension(answer, question);

      case "3": // Audio Listening (Multiple Selection)
        return this.validateAudioListening(answer, question);

      case "4": // Sentence Building
        return this.validateSentenceBuilding(answer, question);

      case "6": // Reference Reading (Multi-part)
        return this.validateReferenceReading(answer, question);

      case "8": // Switch Card Game
        return this.validateSwitchCard(answer, question);

      case "9": // Parts of Speech
        return this.validatePartsOfSpeech(answer, question);

      case "10": // Fill-in-blank Multiple Choice
        return this.validateFillInBlankMC(answer, question);

      case "11": // Speaking Practice
        return this.validateSpeakingPractice(answer, question);

      case "12": // Instructional Content
        return this.validateInstructionalContent(answer, question);

      case "13": // True/False Selection
        return this.validateTrueFalseSelection(answer, question);

      default:
        return {
          isValid: false,
          errors: [`Unknown question type: ${questionType}`],
          suggestions: ["Please contact support"],
        };
    }
  }

  /**
   * Type 1: Multiple Choice - expects a number (index)
   */
  private validateMultipleChoice(
    answer: any,
    question?: any
  ): ValidationResult {
    if (typeof answer !== "number") {
      // Try to convert string to number
      const parsed = parseInt(answer, 10);
      if (!isNaN(parsed)) {
        return {
          isValid: true,
          errors: [],
          formattedAnswer: parsed,
        };
      }
      return {
        isValid: false,
        errors: ["Multiple choice answer must be a number"],
        suggestions: ["Select one option from the list"],
      };
    }

    if (
      question?.options &&
      (answer < 0 || answer >= question.options.length)
    ) {
      return {
        isValid: false,
        errors: [`Answer index ${answer} is out of range`],
        suggestions: [
          `Select an option between 0 and ${question.options.length - 1}`,
        ],
      };
    }

    return { isValid: true, errors: [], formattedAnswer: answer };
  }

  /**
   * Type 2: Reading Comprehension - expects a string
   */
  private validateReadingComprehension(
    answer: any,
    question?: any
  ): ValidationResult {
    if (typeof answer !== "string") {
      // Convert to string if possible
      return {
        isValid: true,
        errors: [],
        formattedAnswer: String(answer),
      };
    }

    if (answer.trim().length === 0) {
      return {
        isValid: false,
        errors: ["Answer cannot be empty"],
        suggestions: ["Please provide your answer"],
      };
    }

    return { isValid: true, errors: [], formattedAnswer: answer.trim() };
  }

  /**
   * Type 3: Audio Listening - expects array of numbers
   */
  private validateAudioListening(
    answer: any,
    question?: any
  ): ValidationResult {
    if (!Array.isArray(answer)) {
      return {
        isValid: false,
        errors: ["Audio listening answer must be an array"],
        suggestions: ["Select one or more options"],
      };
    }

    if (answer.length === 0) {
      return {
        isValid: false,
        errors: ["Please select at least one option"],
        suggestions: ["Listen to the audio and select relevant options"],
      };
    }

    // Validate each selection is a number
    const invalidItems = answer.filter((item) => typeof item !== "number");
    if (invalidItems.length > 0) {
      return {
        isValid: false,
        errors: ["All selections must be numbers"],
        suggestions: ["Invalid selections detected"],
      };
    }

    // Check range if question options provided
    if (question?.options) {
      const outOfRange = answer.filter(
        (idx) => idx < 0 || idx >= question.options.length
      );
      if (outOfRange.length > 0) {
        return {
          isValid: false,
          errors: [`Invalid option indices: ${outOfRange.join(", ")}`],
          suggestions: [
            `Select options between 0 and ${question.options.length - 1}`,
          ],
        };
      }
    }

    return {
      isValid: true,
      errors: [],
      formattedAnswer: [...new Set(answer)].sort(), // Remove duplicates and sort
    };
  }

  /**
   * Type 4: Sentence Building - expects array of strings (ordered segments)
   */
  private validateSentenceBuilding(
    answer: any,
    question?: any
  ): ValidationResult {
    if (!Array.isArray(answer)) {
      return {
        isValid: false,
        errors: ["Sentence building answer must be an array"],
        suggestions: ["Arrange the sentence segments in order"],
      };
    }

    if (answer.length === 0) {
      return {
        isValid: false,
        errors: ["Please arrange at least one segment"],
        suggestions: ["Drag and drop segments to build the sentence"],
      };
    }

    // Validate each segment is a string
    const invalidItems = answer.filter((item) => typeof item !== "string");
    if (invalidItems.length > 0) {
      return {
        isValid: false,
        errors: ["All segments must be strings"],
        suggestions: ["Invalid segments detected"],
      };
    }

    // Check if all required segments are used (if provided)
    if (question?.segments) {
      const requiredCount = question.segments.length;
      if (answer.length !== requiredCount) {
        return {
          isValid: false,
          errors: [`Expected ${requiredCount} segments, got ${answer.length}`],
          suggestions: ["Use all available segments"],
        };
      }
    }

    return { isValid: true, errors: [], formattedAnswer: answer };
  }

  /**
   * Type 6: Reference Reading - expects object with sub-question answers
   */
  private validateReferenceReading(
    answer: any,
    question?: any
  ): ValidationResult {
    if (
      typeof answer !== "object" ||
      answer === null ||
      Array.isArray(answer)
    ) {
      return {
        isValid: false,
        errors: ["Reference reading answer must be an object"],
        suggestions: ["Answer all sub-questions"],
      };
    }

    const errors: string[] = [];
    const formattedAnswer: Record<string, any> = {};

    // Validate sub-questions if provided
    if (question?.questions && Array.isArray(question.questions)) {
      for (let i = 0; i < question.questions.length; i++) {
        const subQ = question.questions[i];
        const subQId = subQ.id || `q${i}`;
        const subAnswer = answer[subQId];

        if (subAnswer === undefined || subAnswer === null || subAnswer === "") {
          if (subQ.required !== false) {
            errors.push(`Question ${i + 1} is required`);
          }
        } else {
          formattedAnswer[subQId] = subAnswer;
        }
      }
    } else {
      // No sub-questions defined, accept any object structure
      Object.assign(formattedAnswer, answer);
    }

    if (Object.keys(formattedAnswer).length === 0) {
      return {
        isValid: false,
        errors: ["Please answer at least one sub-question"],
        suggestions: ["Read the reference material and answer the questions"],
      };
    }

    return {
      isValid: errors.length === 0,
      errors,
      formattedAnswer,
      suggestions:
        errors.length > 0 ? ["Complete all required sub-questions"] : undefined,
    };
  }

  /**
   * Type 8: Switch Card - expects array of matched pairs
   */
  private validateSwitchCard(answer: any, question?: any): ValidationResult {
    if (!Array.isArray(answer)) {
      return {
        isValid: false,
        errors: ["Switch card answer must be an array"],
        suggestions: ["Match the word pairs"],
      };
    }

    // Each item should be a string representing a matched pair
    const invalidItems = answer.filter((item) => typeof item !== "string");
    if (invalidItems.length > 0) {
      return {
        isValid: false,
        errors: ["All matched pairs must be strings"],
        suggestions: ["Invalid matches detected"],
      };
    }

    // Check if all pairs are matched (if word pairs provided)
    if (question?.wordPairs) {
      const expectedPairs = question.wordPairs.length;
      if (answer.length !== expectedPairs) {
        return {
          isValid: false,
          errors: [`Expected ${expectedPairs} matches, got ${answer.length}`],
          suggestions: ["Match all word pairs"],
        };
      }
    }

    return { isValid: true, errors: [], formattedAnswer: answer };
  }

  /**
   * Type 9: Parts of Speech - expects object mapping words to parts
   */
  private validatePartsOfSpeech(answer: any, question?: any): ValidationResult {
    if (
      typeof answer !== "object" ||
      answer === null ||
      Array.isArray(answer)
    ) {
      return {
        isValid: false,
        errors: ["Parts of speech answer must be an object"],
        suggestions: ["Classify each word"],
      };
    }

    const errors: string[] = [];
    const formattedAnswer: Record<string, string> = {};

    // Validate each word classification
    for (const [word, part] of Object.entries(answer)) {
      if (typeof part !== "string" || part.trim().length === 0) {
        errors.push(`Invalid classification for word "${word}"`);
      } else {
        formattedAnswer[word] = part as string;
      }
    }

    if (Object.keys(formattedAnswer).length === 0) {
      return {
        isValid: false,
        errors: ["Please classify at least one word"],
        suggestions: ["Select the part of speech for each word"],
      };
    }

    return {
      isValid: errors.length === 0,
      errors,
      formattedAnswer,
      suggestions:
        errors.length > 0 ? ["Complete all word classifications"] : undefined,
    };
  }

  /**
   * Type 10: Fill-in-blank Multiple Choice - expects object with blank IDs to answers
   */
  private validateFillInBlankMC(answer: any, question?: any): ValidationResult {
    if (
      typeof answer !== "object" ||
      answer === null ||
      Array.isArray(answer)
    ) {
      return {
        isValid: false,
        errors: ["Fill-in-blank answer must be an object"],
        suggestions: ["Fill in all blanks"],
      };
    }

    const errors: string[] = [];
    const formattedAnswer: Record<string, string> = {};

    // Validate blanks if question structure provided
    if (question?.questions && Array.isArray(question.questions)) {
      for (let i = 0; i < question.questions.length; i++) {
        const blankId = `blank_${i}`;
        const blankAnswer = answer[blankId];

        if (
          !blankAnswer ||
          (typeof blankAnswer === "string" && blankAnswer.trim().length === 0)
        ) {
          errors.push(`Blank ${i + 1} is required`);
        } else {
          formattedAnswer[blankId] = String(blankAnswer).trim();
        }
      }
    } else {
      // Accept any structure if no question definition
      Object.entries(answer).forEach(([key, value]) => {
        if (value && String(value).trim().length > 0) {
          formattedAnswer[key] = String(value).trim();
        }
      });
    }

    if (Object.keys(formattedAnswer).length === 0) {
      return {
        isValid: false,
        errors: ["Please fill in at least one blank"],
        suggestions: ["Select the correct option for each blank"],
      };
    }

    return {
      isValid: errors.length === 0,
      errors,
      formattedAnswer,
      suggestions: errors.length > 0 ? ["Complete all blanks"] : undefined,
    };
  }

  /**
   * Type 11: Speaking Practice - expects Blob, string URL, or null
   */
  private validateSpeakingPractice(
    answer: any,
    question?: any
  ): ValidationResult {
    // Null means no recording yet
    if (answer === null) {
      return {
        isValid: false,
        errors: ["No recording submitted"],
        suggestions: ["Record your voice reading the text"],
      };
    }

    // Check if it's a Blob (audio recording)
    if (answer instanceof Blob) {
      if (answer.size === 0) {
        return {
          isValid: false,
          errors: ["Recording is empty"],
          suggestions: ["Record your voice before submitting"],
        };
      }
      return { isValid: true, errors: [], formattedAnswer: answer };
    }

    // Check if it's a string (URL or base64)
    if (typeof answer === "string") {
      if (answer.trim().length === 0) {
        return {
          isValid: false,
          errors: ["Recording URL is empty"],
          suggestions: ["Record your voice before submitting"],
        };
      }
      return { isValid: true, errors: [], formattedAnswer: answer };
    }

    return {
      isValid: false,
      errors: ["Invalid recording format"],
      suggestions: ["Please record your voice or try again"],
    };
  }

  /**
   * Type 12: Instructional Content - no answer required
   */
  private validateInstructionalContent(
    answer: any,
    question?: any
  ): ValidationResult {
    // Instructional content doesn't require an answer - always valid
    return {
      isValid: true,
      errors: [],
      formattedAnswer: null,
    };
  }

  /**
   * Type 13: True/False Selection - expects array of indices
   */
  private validateTrueFalseSelection(
    answer: any,
    question?: any
  ): ValidationResult {
    if (!Array.isArray(answer)) {
      return {
        isValid: false,
        errors: ["True/False selection must be an array"],
        suggestions: ["Select the statements you believe are true"],
      };
    }

    // Validate each selection is a number
    const invalidItems = answer.filter((item) => typeof item !== "number");
    if (invalidItems.length > 0) {
      return {
        isValid: false,
        errors: ["All selections must be numbers"],
        suggestions: ["Invalid selections detected"],
      };
    }

    // Check if correct number selected (only if specified)
    if (
      question?.numberOfCorrect !== undefined &&
      answer.length !== question.numberOfCorrect
    ) {
      return {
        isValid: false,
        errors: [
          `Please select exactly ${question.numberOfCorrect} statement(s)`,
        ],
        suggestions: [
          `You selected ${answer.length}, but ${question.numberOfCorrect} are correct`,
        ],
      };
    }

    // Validate range if statements provided
    if (question?.statements) {
      const outOfRange = answer.filter(
        (idx) => idx < 0 || idx >= question.statements.length
      );
      if (outOfRange.length > 0) {
        return {
          isValid: false,
          errors: [`Invalid statement indices: ${outOfRange.join(", ")}`],
          suggestions: [
            `Select statements between 0 and ${question.statements.length - 1}`,
          ],
        };
      }
    }

    return {
      isValid: true,
      errors: [],
      formattedAnswer: [...new Set(answer)].sort((a, b) => a - b), // Remove duplicates and sort
    };
  }

  /**
   * Save answer with validation
   */
  public saveAnswer(
    questionId: string,
    questionType: string,
    answer: any,
    question?: any
  ): boolean {
    const validation = this.validateAnswer(questionType, answer, question);

    if (!validation.isValid) {
      console.warn(
        `[AnswerState] Invalid answer for question ${questionId}:`,
        validation.errors
      );
      return false;
    }

    // Use formatted answer if available, otherwise use original
    const finalAnswer =
      validation.formattedAnswer !== undefined
        ? validation.formattedAnswer
        : answer;

    this.userAnswers.set(questionId, finalAnswer);
    this.validationCache.set(questionId, validation);

    console.log(
      `[AnswerState] Saved answer for question ${questionId} (type ${questionType}):`,
      finalAnswer
    );
    return true;
  }

  /**
   * Get answer for a specific question
   */
  public getAnswer(questionId: string): any {
    return this.userAnswers.get(questionId);
  }

  /**
   * Get all answers
   */
  public getAllAnswers(): Map<string, any> {
    return new Map(this.userAnswers);
  }

  /**
   * Check if question has a valid answer
   */
  public hasValidAnswer(questionId: string): boolean {
    const answer = this.userAnswers.get(questionId);
    return (
      answer !== undefined &&
      answer !== null &&
      answer !== "SKIPPED" &&
      answer !== ""
    );
  }

  /**
   * Get validation result for a question
   */
  public getValidation(questionId: string): ValidationResult | undefined {
    return this.validationCache.get(questionId);
  }

  /**
   * Clear answer for a question
   */
  public clearAnswer(questionId: string): void {
    this.userAnswers.delete(questionId);
    this.validationCache.delete(questionId);
  }

  /**
   * Clear all answers
   */
  public clearAllAnswers(): void {
    this.userAnswers.clear();
    this.validationCache.clear();
  }

  /**
   * Get statistics about answers
   */
  public getStatistics(): {
    total: number;
    answered: number;
    skipped: number;
    valid: number;
    invalid: number;
  } {
    let answered = 0;
    let skipped = 0;
    let valid = 0;
    let invalid = 0;

    this.userAnswers.forEach((answer, questionId) => {
      if (answer === "SKIPPED") {
        skipped++;
      } else if (answer !== undefined && answer !== null && answer !== "") {
        answered++;
        const validation = this.validationCache.get(questionId);
        if (validation?.isValid) {
          valid++;
        } else {
          invalid++;
        }
      }
    });

    return {
      total: this.userAnswers.size,
      answered,
      skipped,
      valid,
      invalid,
    };
  }

  /**
   * Export answers for submission
   */
  public exportForSubmission(): Array<{ questionId: string; answer: any }> {
    const submission: Array<{ questionId: string; answer: any }> = [];

    this.userAnswers.forEach((answer, questionId) => {
      if (answer !== undefined && answer !== null && answer !== "SKIPPED") {
        submission.push({ questionId, answer });
      }
    });

    return submission;
  }
}
